import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { WorkOrderService } from '../../core/api/work-order.service';
import { WorkOrder } from '../../shared/models/work-order';
import { statusTone } from '../../shared/status-tone';
import { AppNavComponent } from '../../shared/app-nav.component';
import { BackButtonComponent } from '../../shared/back-button.component';
import { TranslatePipe } from '../../shared/translate.pipe';
import { StatusLabelPipe } from '../../shared/status-label.pipe';
import { MneDatePipe } from '../../shared/mne-date.pipe';

@Component({
  standalone: true,
  imports: [RouterLink, AppNavComponent, BackButtonComponent, TranslatePipe, StatusLabelPipe, MneDatePipe],
  template: `
        <app-nav></app-nav>
    <main class="page-shell">
      <header class="topbar">
        <app-back></app-back>
        <h1>{{ 'wo.detailTitle' | t }}</h1>
      </header>
      @if (item(); as a) {
        <section class="detail-card">
          <div class="detail-heading">
            <div>
              <p class="eyebrow">#{{ a.Id }}</p>
              <h2>{{ a.CustomerName || ('wo.fallback' | t) }}</h2>
            </div>
            <span class="status-pill" [class]="tone(a.StatusName)">{{ (a.StatusName | statusLabel) || ('status.draft' | t) }}</span>
          </div>
          <dl>
            <dt>{{ 'wo.lubarisNumber' | t }}</dt><dd>{{ a.LubarisNumber ?? '—' }}</dd>
            <dt>{{ 'wo.year' | t }}</dt><dd>{{ a.Year || '—' }}</dd>
            <dt>{{ 'wo.shift' | t }}</dt><dd>{{ a.ShiftName || '—' }}</dd>
            <dt>{{ 'wo.startTime' | t }}</dt><dd>{{ a.StartTime | mneDate }}</dd>
            <dt>{{ 'wo.endTime' | t }}</dt><dd>{{ a.EndTime | mneDate }}</dd>
            <dt>{{ 'common.remarks' | t }}</dt><dd>{{ a.Remarks || '—' }}</dd>
            <dt>{{ 'wo.sentDate' | t }}</dt><dd>{{ a.SentDate | mneDate }}</dd>
            <dt>{{ 'common.createdBy' | t }}</dt><dd>{{ a.CreatedByName || '—' }}</dd>
          </dl>

          @if (a.Rows?.length) {
            <h3 class="section-title">{{ 'wo.rows' | t }}</h3>
            <section class="list">
              @for (row of a.Rows; track row.Id) {
                <a class="list-row" [class]="'tone-' + tone(row.StatusName)" [routerLink]="['/work-orders', a.Id, 'rows', row.Id]">
                  <div>
                    <span class="row-id">#{{ row.Id }}</span>
                    <strong>{{ row.ServiceRequestTypeName || ('wo.rowFallback' | t) }}</strong>
                    <span>{{ row.CargoTypeName || '' }}</span>
                  </div>
                  <div class="row-meta">
                    <span class="status-pill" [class]="tone(row.StatusName)">{{ row.StatusName | statusLabel }}</span>
                    <b>›</b>
                  </div>
                </a>
              }
            </section>
          }
        </section>
        @if (error()) { <p class="error notice">{{ error() | t }}</p> }
      } @else if (error()) { <p class="error notice">{{ error() | t }}</p> } @else { <div class="skeleton-detail">
        <div class="skeleton-heading"></div>
        <div class="skeleton-line w-70"></div>
        <div class="skeleton-line w-55"></div>
        <div class="skeleton-line w-85"></div>
        <div class="skeleton-line w-40"></div>
      </div> }
    </main>
  `
})
export class WorkOrderDetailPage implements OnInit {
  readonly item = signal<WorkOrder | null>(null);
  readonly error = signal('');

  constructor(private readonly route: ActivatedRoute, private readonly api: WorkOrderService) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!id) { this.error.set('wo.missingId'); return; }
    this.api.get(id).subscribe({
      next: item => this.item.set(item),
      error: err => this.error.set(err?.error?.Message ?? err?.error?.message ?? 'wo.detailLoadError')
    });
  }
  tone(status?: string | null): string { return statusTone(status); }
}
